/**
 * PartsArrivingWidget — Dashboard card showing parts arriving this week.
 *
 * Lists bookings expecting part deliveries, grouped by date, with supplier info.
 */

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import type { PartsArrivingItem } from '@/hooks/useDashboard';
import { intlLocale } from '@/utils/locale';

interface PartsArrivingWidgetProps {
  items: PartsArrivingItem[];
}

export function PartsArrivingWidget({ items }: PartsArrivingWidgetProps) {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <LocalShippingIcon color="primary" />
          <Typography variant="h6">{t('partsArrivingThisWeek')}</Typography>
        </Box>

        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('noPartsArriving')}
          </Typography>
        ) : (
          <List disablePadding>
            {items.map((item) => (
              <ListItemButton
                key={item.bookingId}
                onClick={() => navigate(`/workorders?id=${item.bookingId}`)}
                sx={{ borderRadius: 1, mb: 0.5, px: 1 }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Chip
                    label={new Date(item.expectedDeliveryDate).toLocaleDateString(intlLocale(), { weekday: 'short', day: 'numeric', month: 'numeric' })}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem' }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight={600}>{item.vehicleRegNumber}</Typography>
                      <Typography variant="body2" color="text.secondary">{item.customerName}</Typography>
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {item.supplierName} — {item.parts.map(p => p.partName).join(', ')}
                    </Typography>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
}
